import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type JSX,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import './Lectern.css';

interface LecternProps {
  /**
   * Fires when the local viewer commits a non-empty clue. Production wiring
   * dispatches `session.send('submitClue', { text })`. The component clears
   * the input synchronously after invoking the handler so the keyboard's
   * Return doesn't double-fire on slow networks.
   */
  onSubmit: (text: string) => void;
  /**
   * Maximum allowed clue length. UX cap is 30 characters; the server enforces
   * a defensive 80-character ceiling. Stored as `maxlength` on the input —
   * typing past the cap is silently dropped by the browser, no error toast.
   */
  maxLength?: number;
  /**
   * Cold-Arrival Rule: `true` on a reconnect/boot directly into the local
   * player's own turn. Suppresses the mount choreography (rule-draw +
   * ink-ins) so the lectern is simply present at first paint. Mid-phase
   * handoffs mount with `false` and play the entrance normally.
   */
  suppressEntry?: boolean;
}

/**
 * The lectern: a single horizontal row [Cutive Mono input] [Submit] pinned to
 * the verso foot via `margin-top: auto`. No surrounding chrome — no twin-rule
 * frame, no caption, no character counter. Rendered only when the local
 * viewer is the active speaker (`CluegivingPhase` owns that conditional;
 * this component just renders the row).
 *
 * The writing line is a separate `__line` element (not a border on the input)
 * so the turn-arrival beat can DRAW it across the page — the pen rules the
 * line, then the italic placeholder and Submit ink in behind it. The line
 * carries the focus treatment the input's border used to: ash at rest,
 * deepening to ink with the stacked 1px shadow on focus-within (the
 * typewriter platen pressing harder). The input is the only line on the page
 * that reads as type-ready; the rest of the verso is set type.
 *
 * Two micro-beats ride the typed text (the platen idiom):
 * - each forward keystroke lands with a 1.3px settle (`--strike`),
 * - on submit the written line lifts off the field (`__exit`) as the same
 *   text inks in on the ClueSlip beneath the player's portrait.
 */
export function Lectern({
  onSubmit,
  maxLength = 30,
  suppressEntry = false,
}: LecternProps): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [exitText, setExitText] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(trimmed);
    setValue('');
    /* The press-into-the-page beat: the submitted line lifts off the field
       while the real ClueSlip inks in beneath the portrait. Transient and
       aria-hidden — the input itself cleared synchronously above. */
    setExitText(trimmed);
  }, [canSubmit, onSubmit, trimmed]);

  useEffect(() => {
    if (exitText === null) return undefined;
    const handle = window.setTimeout(() => setExitText(null), 450);
    return () => window.clearTimeout(handle);
  }, [exitText]);

  /* The platen strike: each forward keystroke restarts a 70ms settle on the
     input. Done with classList (not state) so a fast typist re-triggers the
     animation per character without a render per frame. */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      if (next.length > value.length && inputRef.current) {
        const el = inputRef.current;
        el.classList.remove('susdle-lectern__input--strike');
        void el.offsetWidth;
        el.classList.add('susdle-lectern__input--strike');
      }
      setValue(next);
    },
    [value.length],
  );

  /* Desktop submit affordance: Enter on the input. The button click handler
     is the canonical path on both desktop and mobile (virtual keyboard
     Return behavior varies across platforms — brief §7). */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  /* The lectern is a `<form>` so the submit button + Enter both wire through
     a single React event. preventDefault stops the browser from initiating
     a page reload navigation in dev. */
  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <form
      className="susdle-lectern"
      data-suppress-entry={suppressEntry ? 'yes' : 'no'}
      onSubmit={handleFormSubmit}
    >
      <div className="susdle-lectern__field">
        <input
          ref={inputRef}
          className="susdle-lectern__input"
          type="text"
          maxLength={maxLength}
          placeholder={t('cluegiving.lectern.placeholder')}
          aria-label={t('cluegiving.lectern.ariaLabel')}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          data-testid="lectern-clue-input"
          /* The brief says no character counter, no caption. The placeholder
             plus the visible 1px ruled line carry the affordance. */
        />
        <span aria-hidden className="susdle-lectern__line" />
        {exitText !== null ? (
          <span aria-hidden className="susdle-lectern__exit">
            {exitText}
          </span>
        ) : null}
      </div>
      <button
        type="submit"
        className="susdle-action susdle-action--compact susdle-action--hit-area susdle-lectern__submit"
        disabled={!canSubmit}
        data-testid="lectern-submit"
      >
        {t('cluegiving.lectern.submit')}
      </button>
    </form>
  );
}
